import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { TextField, InputAdornment, Box, Checkbox, useMediaQuery, useTheme, FormControlLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useEntries, getDefaultFilters } from '../../context/EntriesContext';

const Divider = styled('div')({
    width: '12px',
    height: '1px',
    backgroundColor: 'white',
    margin: '0 8px',
});

export default function Filters() {
    const { t } = useTranslation("common");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {
        entriesData: { totalCount },
        filters,
        setFilters,
        showImages,
        setShowImages,
    } = useEntries();

    const [dateBeforeReset, setDateBeforeReset] = useState({ minDate: filters.minDate, maxDate: filters.maxDate });
    const [allEntriesChecked, setAllEntriesChecked] = useState(false);

    // reset filters at unmount
    useEffect(() => {
        return () => {
            setFilters(getDefaultFilters());
        };
    }, [setFilters]);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        let minDate = filters.minDate;
        let maxDate = filters.maxDate;

        if (minDate === getDefaultFilters().minDate && maxDate === getDefaultFilters().maxDate) {
            minDate = 0;
            maxDate = 0;
        }

        if (value) {
            if (filters.minDate !== 0 && filters.maxDate !== 0) {
                setDateBeforeReset({
                    minDate: filters.minDate,
                    maxDate: filters.maxDate,
                });
            }
            setFilters({ ...filters, licensePlate: value, minDate, maxDate });
        } else {
            setFilters({
                ...filters,
                licensePlate: '',
                minDate: dateBeforeReset.minDate,
                maxDate: dateBeforeReset.maxDate,
            });
        }
    }, [setFilters, filters, dateBeforeReset]);

    // if more than 1 hour passed since last update, reset filters
    useEffect(() => {
        const lastUpdate = sessionStorage.getItem('lastTableUpdate');
        if (lastUpdate) {
            const lastDate = new Date(lastUpdate);
            const now = new Date();
            const diffMs = now.getTime() - lastDate.getTime();
            const diffHours = diffMs / (1000 * 60 * 60);
            if (diffHours > 1) {
                setFilters(getDefaultFilters());
            }
        }
        const now = new Date();
        sessionStorage.setItem('lastTableUpdate', now.toISOString());
    }, [setFilters]);

    const onShowAllEntriesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;

        if (checked) {
            setDateBeforeReset({
                minDate: filters.minDate,
                maxDate: filters.maxDate,
            });

            setFilters({ ...filters, minDate: 0, maxDate: 0 });
            setAllEntriesChecked(true);
        } else {
            setFilters({
                ...filters,
                minDate: dateBeforeReset.minDate,
                maxDate: dateBeforeReset.maxDate,
            });
            setAllEntriesChecked(false);
        }
    }, [filters, dateBeforeReset, setFilters]);

    const renderSearchField = useMemo(() => (
        <TextField
            variant="outlined"
            placeholder={t("search.filters.searchByLicensePlate")}
            value={filters.licensePlate}
            onChange={handleSearchChange}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <SearchIcon />
                    </InputAdornment>
                ),
                inputProps: { sx: { textAlign: 'left', pl: '14px', pt: '8.5px', pb: '8.5px' } },
            }}
            style={{ width: '180px', borderRadius: '4px' }}
        />
    ), [t, filters, handleSearchChange]);

    const renderDatePickers = useMemo(() => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DatePicker
                label={t("search.filters.from")}
                value={filters.minDate ? dayjs(filters.minDate) : null}
                onChange={date => setFilters({ ...filters, minDate: date?.valueOf() || 0 })}
                maxDate={filters.maxDate ? dayjs(Math.min(filters.maxDate, dayjs().valueOf())) : dayjs()}
                slotProps={{
                    textField: {
                        size: "small",
                        error: false,
                    },
                }}
            />
            <Divider />
            <DatePicker
                label={t("search.filters.to")}
                value={filters.maxDate ? dayjs(filters.maxDate) : null}
                onChange={(date) => setFilters({ ...filters, maxDate: date?.valueOf() || 0 })}
                maxDate={dayjs()}
                slotProps={{
                    textField: {
                        size: "small",
                        error: false,
                    },
                }}
            />
        </Box>
    ), [filters, t, setFilters]);

    const renderAllEntriesCheckbox = useMemo(() => (
        <FormControlLabel
            control={
                <Checkbox
                    checked={allEntriesChecked}
                    onChange={onShowAllEntriesChange}
                    id="all-entries-checkbox"
                />
            }
            label={t("search.filters.allEntries")}
            sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
        />
    ), [allEntriesChecked, onShowAllEntriesChange, t]);

    const renderShowImagesCheckbox = useMemo(() => {
        return (
            <FormControlLabel
                control={
                    <Checkbox
                        checked={showImages}
                        onChange={e => setShowImages(e.target.checked)}
                        id="show-images-checkbox"
                    />
                }
                label={t("search.filters.showImages")}
                sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
            />
        )
    }, [showImages, t, setShowImages]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                background: '#000',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minWidth: '100%',
                    backgroundColor: 'var(--template-palette-AppBar-darkBg, var(--AppBar-background))',
                    borderRadius: '4px',
                    padding: isMobile ? '10px 0 10px' : '10px 16px',
                }}>
                <Box sx={{ display: 'flex', flexFlow: 'row', gap: isMobile ? '5px' : '10px', paddingBottom: isMobile ? '10px' : '0', alignItems: 'center' }}>
                    {renderSearchField}
                    {renderDatePickers}
                    {!isMobile && renderAllEntriesCheckbox}
                </Box>
                <Box sx={{ display: 'flex', flexFlow: 'row', alignItems: 'center', gap: isMobile ? 0 : '10px' }}>
                    {renderShowImagesCheckbox}
                    {isMobile && renderAllEntriesCheckbox}
                    {isMobile && <span style={{ whiteSpace: 'nowrap', width: '100px', textAlign: 'center' }}>{t("search.filters.results", { nr: totalCount })}</span>}
                </Box>
            </Box>
        </div>
    );
}